<template>
    <App v-if="project && project.qrcodeObject && !project.qrcodeObject.project.isUsingLandingFullHtml"></App>
    <iframe
        allowfullscreen
        v-if="project && project.qrcodeObject && project.qrcodeObject.project.isUsingLandingFullHtml"
        :srcdoc="'<script>' + iframeScriptCode + '</script>' + iframeCode"
    ></iframe>
    <InactiveLandingPage v-if="isBanned" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { State } from "./store";
import App from "./App.vue";
import getQrCodeService, { TrackingEvents } from "@/services/QrCodeService";
import scriptV1 from "./apis/v1.js";
import { Fingerprint2, isDemo, isMobile } from "./utils";
import { QrCodeObject } from "./models/QrCodeObject";
import InactiveLandingPage from "@/views/InactiveLandingPage.vue";
import { getApiEndpoints, getGeoInfo, sendLog } from "./utils/utils";

export default defineComponent({
    name: "PreApp",
    components: {
        App,
        InactiveLandingPage
    },
    data() {
        return {
            iframeCode: "",
            iframeScriptCode: "",
            sendLog: false
        };
    },
    mounted() {
        const searchParams = new URLSearchParams(window.location.search);
        const qrcodeId = searchParams.get("qr") || "";

        if (!qrcodeId || qrcodeId.length < 4) {
            this.$store.dispatch("qrcode/displayError", {
                title: "errors.qrcodeNotFoundTitle",
                text: "errors.qrcodeNotFoundText"
            });
            return;
        }

        try {
            this.$store.dispatch("qrcode/load", qrcodeId);

            if (!isDemo() && isMobile()) {
                // this.$store.dispatch("qrcode/logScan", qrcodeId);
            }
        } catch (e) {
            console.error(e);
        }
    },
    computed: {
        ...mapState({
            project: (state: State) => state.qrcode,
            isBanned:(state:State)=> state.qrcode.isBanned
        })
    },
    watch: {
        project: {
            immediate: true,
            deep: true,
            async handler() {
                if (!this.project || !this.project.qrcodeObject) {
                    return;
                }
                const QR = this.project.qrcodeObject;
                let fingerprint = "";
                let cityName = "";
                let countryName = "";
                if (QR.project) {
                    if (!isDemo() && isMobile()) {
                        fingerprint = await this.generateFingerprint();
                        Fingerprint2.get(components => {
                            const values = components.map(component => component.value);
                            Fingerprint2.x64hash128(values.join(""), 31);
                        });
                        const geoData= await getGeoInfo();
                        cityName = geoData.cityName;
                        countryName = geoData.countryName;
                        if(!this.sendLog)
                        sendLog(TrackingEvents.OPEN, QR);
                        this.sendLog = true;
                    }
                    if (QR.project.isUsingLandingFullHtml) {
                        this.iframeCode = QR.template.landingFullHtml;
                        this.iframeScriptCode = this.buildScriptV1(QR, scriptV1, fingerprint, cityName, countryName);
                        window.addEventListener('message',(ev)=>{this.handleIframeMessage(ev)})
                        document.title = QR.project.name;
                        const link: HTMLLinkElement =
                            document.querySelector("link[rel*='icon']") ||
                            document.createElement("link");
                        link.type = "image/x-icon";
                        link.rel = "shortcut icon";
                        link.href = QR.template.landingFaviconDataurl;
                        document.getElementsByTagName("head")[0].appendChild(link);
                    }
                }
            }
        }
    },
    methods: {
        getEnvPath() {
            return (
                "var STQR_API_ENV = " +
                JSON.stringify({
                    serverRoot: getApiEndpoints().API_ROOT,
                    bearMedia: getApiEndpoints().BEAR_MEDIA,
                    mediaRoot:getApiEndpoints().API_BASE,
                    statsUrl: getApiEndpoints().STATS
                })
            );
        },
        async  generateFingerprint(): Promise<string> {
            return new Promise((resolve, reject) => {
                if (typeof Fingerprint2 === 'undefined') {
                    reject(new Error("Fingerprint2 error."));
                    return;
                }

                Fingerprint2.get(components => {
                    const values = components.map(component => component.value);
                    const fingerprint = Fingerprint2.x64hash128(values.join(""), 31);
                    resolve(fingerprint);
                });
            });
        },
        
        buildScriptV1(QR: QrCodeObject, script: string, fingerprint: string, cityName: string, countryName: string) {

            const urlParams = new URLSearchParams(window.location.search);
            const obj = {
                shortlinkPublicCryptoId: QR.shortlink.publicCryptoId,
                shortlinkId: QR.shortlink.id,
                projectId: QR.project.id,
                userId: QR.project.userId,
                videoUrl: QR.currentVideoUrl,
                audioUrl: QR.currentAudioUrl,
                photos: QR.currentPhotos,
                maxBurningVideoLength: QR.project.burningVideoMaxLength,
                projectReported: false, // TO_DO => change value after BE task
                projectBanned: false, // TO_DO => change value after BE task
                status: QR.isBurnt ? "BURNT" : "BLANK",
                reBurn: false,
                canAnyOneChangeBurn: QR.project.isAnyoneCanChangeBurn,
                fp: fingerprint,
                city: cityName,
                country: countryName,
                organizationId: QR.project.organizationId,
                langUrl: urlParams.get('lang'),
                isThankYouEmailSent: QR.isThankYouEmailSent,
                isThankYouEmailAllowed: QR.isThankYouEmailAllowed

            };
            if(QR.isBurnt && QR.currentVideoUrl.length){
             obj.videoUrl = getQrCodeService().getFullVideoPath(QR.currentVideoUrl);
            }
            return this.getEnvPath() + script + "(" + JSON.stringify(obj) + ")";
        },
        
        /*
         * insert gtm script to the head to avoid --Unallowed document protocol-- related to unsafe url of iframe
         * @param event Message event
         */
        handleIframeMessage(event: MessageEvent) {
            if(event.origin !== window.location.origin) return;
            const data = event.data;
            if (data && data.command === 'insertScript' && data.gtmScript && data.gtmNoScript) {
                const script = document.createElement('script');
                const noscript = document.createElement('noscript');
                script.textContent = data.gtmScript;
                noscript.textContent = data.gtmNoScript;
                document.head.prepend(script);
                document.body.prepend(noscript);
                console.warn("GTM script successfully inserted via postMessage.");
            }
        }
    }
});
</script>

<style>
iframe {
    border: none;
    width: 100%;
    height: 100vh;
}
</style>
